import React from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, CircleDashed, ShieldCheck } from 'lucide-react';

import type {
  GenerationAlternativeRow,
  GenerationAlternativesMatrixResult,
} from '../../../lib/generationImpact/generationAlternatives';
import type { AlternativeParcelFitSummary } from '../../../lib/generationImpact/generationLayoutSearch';

interface GenerationAlternativesMatrixProps {
  matrix: GenerationAlternativesMatrixResult;
  readOnly?: boolean;
  actionsDisabled?: boolean;
  appliedStatus?: string | null;
  /** Optional per-row parcel-fit verdicts against the current layout. */
  parcelFits?: ReadonlyMap<string, AlternativeParcelFitSummary>;
  onApply: (row: GenerationAlternativeRow) => void;
}

function parcelFitLabel(fit: AlternativeParcelFitSummary): string {
  if (fit.status === 'indeterminate') return 'Unverified';
  return `${fit.blocksContained}/${fit.blocksPlaced} blocks`;
}

function parcelFitTone(fit: AlternativeParcelFitSummary): string {
  return fit.status === 'fits'
    ? 'text-success'
    : fit.status === 'partial' ? 'text-danger' : 'text-text-muted';
}

function number(value: number, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
    minimumFractionDigits: maximumFractionDigits,
  }).format(value);
}

function signed(value: number): string {
  return `${value > 0 ? '+' : ''}${number(value, 1)}`;
}

function compactAnnualTonnes(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${number(value / 1_000_000, 2)}m`;
  if (Math.abs(value) >= 1_000) return `${number(value / 1_000, 0)}k`;
  return number(value, 0);
}

function rowTone(row: GenerationAlternativeRow): string {
  if (row.capacity.nMinusOneReserveVerdict === 'pass') return 'border-l-success';
  if (row.capacity.nMinusOneVerdict === 'pass') return 'border-l-warning';
  return 'border-l-danger';
}

/**
 * Dense protection-engineering schedule for deterministic capacity alternatives.
 * Every metric is supplied by the canonical generation solver; this component
 * formats and applies rows but performs no engineering calculation.
 */
export function GenerationAlternativesMatrix({
  matrix,
  readOnly = false,
  actionsDisabled = false,
  appliedStatus = null,
  parcelFits,
  onApply,
}: GenerationAlternativesMatrixProps): React.JSX.Element {
  if (matrix.status === 'invalid') {
    return (
      <section
        aria-label="Generation alternatives unavailable"
        className="border border-warning/50 bg-warning/5 px-4 py-3 rounded-md"
        data-testid="generation-alternatives-unavailable"
      >
        <h2 className="text-xs font-bold uppercase tracking-wider">Capacity alternatives unavailable</h2>
        <p className="mt-1 text-[10px] text-text-secondary">{matrix.errors.join(' ')}</p>
      </section>
    );
  }

  return (
    <section
      aria-label="Generation alternatives matrix"
      className="min-w-0 max-w-full border-2 border-border bg-card"
      data-testid="generation-alternatives-matrix"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
            <ShieldCheck className="h-4 w-4 text-primary" />
            N-1 capacity alternatives
          </h2>
          <p className="mt-1 max-w-4xl text-[10px] text-text-muted">
            Current basis plus minimum viable 2-, 3-, and 4-block screens with capture off/on. Optimized duties are rounded upward to {number(matrix.sizingIncrementMW)} MW and re-solved; they are planning archetypes, not vendor-available ratings.
          </p>
        </div>
        <div className="border border-border bg-background px-3 py-2 text-right text-[9px] uppercase tracking-wider text-text-muted rounded-md">
          <span className="block font-bold text-text-primary">Design contingency</span>
          Largest independent block out
        </div>
      </div>

      <p
        aria-atomic="true"
        aria-live="polite"
        className="sr-only"
        data-testid="generation-alternative-apply-status"
        role="status"
      >
        {appliedStatus ?? ''}
      </p>

      <div className="grid gap-3 border-b border-border bg-background p-3 sm:grid-cols-2 2xl:grid-cols-3" data-testid="generation-alternative-cards">
        {matrix.rows.map(row => {
          const isCurrent = row.kind === 'current';
          const generationAndCcsWater = row.water.generationConsumptionGalPerDay
            + row.water.ccsConsumptionGalPerDay;
          return (
            <article
              className={`flex min-w-0 flex-col border border-border border-l-4 bg-card p-4 ${rowTone(row)} ${isCurrent ? 'ring-1 ring-inset ring-primary' : ''} rounded-lg`}
              data-alternative-card-id={row.id}
              key={row.id}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="flex items-center gap-2 text-xs font-bold text-text-primary">
                    {isCurrent
                      ? <CircleDashed className="h-3.5 w-3.5 text-primary" />
                      : row.capacity.nMinusOneReserveVerdict === 'pass'
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                        : <AlertTriangle className="h-3.5 w-3.5 text-warning" />}
                    {isCurrent
                      ? 'Current working basis'
                      : row.kind === 'equipment-count'
                        ? `${row.blockCount} × ${row.equipmentModel} envelope`
                        : `${row.blockCount}-block reserve duty`}
                  </h3>
                  <p className="mt-1 text-lg font-bold tabular-nums text-text-primary">{row.blockCount} × {number(row.isoGrossMWPerUnit)} <small className="text-[9px] font-normal text-text-muted">ISO MW</small></p>
                </div>
                <span className={`border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${row.ccsEnabled ? 'border-success/40 bg-success/10 text-success' : 'border-border text-text-muted'} rounded-md`}>CCS {row.ccsEnabled ? 'on' : 'off'}</span>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-px bg-border text-[9px]">
                <div className="bg-background p-2"><dt className="text-text-muted">N-1 reserve margin</dt><dd className={`mt-1 font-bold ${row.capacity.nMinusOneReserveVerdict === 'pass' ? 'text-success' : 'text-danger'}`}>{signed(row.capacity.nMinusOneReserveMarginMW)} MW</dd></div>
                <div className="bg-background p-2"><dt className="text-text-muted">Independent load served</dt><dd className="mt-1 font-bold text-text-primary">{row.result.reliability.probabilityLoadServed == null ? 'Not established' : `${number(row.result.reliability.probabilityLoadServed * 100, 3)}%`}</dd></div>
                <div className="bg-background p-2"><dt className="text-text-muted">Peak gas</dt><dd className="mt-1 font-bold text-text-primary">{number(row.gas.peakDthPerDay)} Dth/d</dd></div>
                <div className="bg-background p-2"><dt className="text-text-muted">Gen. + CCS water</dt><dd className="mt-1 font-bold text-text-primary">{number(generationAndCcsWater)} gal/d</dd></div>
                <div className="bg-background p-2"><dt className="text-text-muted">Footprint</dt><dd className="mt-1 font-bold text-text-primary">{number(row.footprint.totalAcres, 1)} ac</dd></div>
                <div className="bg-background p-2"><dt className="text-text-muted">Modeled on-site annual CO₂</dt><dd className="mt-1 font-bold text-text-primary">{compactAnnualTonnes(row.emissions.capturedCo2TonnesPerYear)} captured</dd></div>
                {parcelFits?.get(row.id) && (
                  <div className="bg-background p-2"><dt className="text-text-muted">Parcel fit</dt><dd className={`mt-1 font-bold ${parcelFitTone(parcelFits.get(row.id)!)}`}>{parcelFitLabel(parcelFits.get(row.id)!)}</dd></div>
                )}
              </dl>
              <p className="mt-2 text-[9px] text-text-muted">{isCurrent
                ? 'Entered rating.'
                : row.kind === 'equipment-count'
                  ? 'Fixed OEM block rating — only the block count varies; verdicts are exact screens.'
                  : `Exact reserve screen ${number(row.exactReserveMinimumIsoGrossMWPerUnit ?? 0, 1)} MW/block; rounded to ${matrix.sizingIncrementMW} MW.`}</p>
              <button
                type="button"
                aria-label={isCurrent ? 'Current generation alternative' : `Apply ${row.label}`}
                disabled={readOnly || actionsDisabled || isCurrent}
                onClick={() => onApply(row)}
                className="mt-3 inline-flex items-center justify-center gap-1 border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary disabled:border-border disabled:text-text-muted rounded-md"
              >
                {isCurrent ? 'Current basis' : 'Use this alternative'}
                {!isCurrent && <ArrowRight className="h-3 w-3" />}
              </button>
            </article>
          );
        })}
      </div>

      <details className="border-t border-border">
        <summary className="cursor-pointer px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-text-secondary outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">Detailed alternative comparison · resources, reliability &amp; emissions</summary>
        <div
          aria-label="Generation alternatives comparison; scroll horizontally for all columns"
          className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain border-t border-border"
          data-testid="generation-alternatives-table-scroll"
          role="region"
          tabIndex={0}
        >
        <table className="w-full min-w-[1580px] table-fixed text-left text-[10px]" data-testid="generation-alternatives-table">
          <caption className="sr-only">
            Current generation basis and reserve-sized two-, three-, and four-block alternatives with reliability, resource impacts, and working-basis actions.
          </caption>
          <thead className="border-b border-border bg-background text-[9px] uppercase tracking-wider text-text-muted">
            <tr>
              <th className="w-[220px] px-3 py-2">Configuration</th>
              <th className="w-[135px] px-3 py-2 text-right">ISO duty</th>
              <th className="w-[115px] px-3 py-2">Capture</th>
              <th className="w-[135px] px-3 py-2 text-right">N-1 net</th>
              <th className="w-[145px] px-3 py-2 text-right">Reserve margin</th>
              <th className="w-[145px] px-3 py-2 text-right">Independent load served</th>
              <th className="w-[145px] px-3 py-2 text-right">Peak gas</th>
              <th className="w-[160px] px-3 py-2 text-right">Gen. + CCS water</th>
              <th className="w-[175px] px-3 py-2 text-right">Modeled on-site captured / residual CO₂</th>
              <th className="w-[115px] px-3 py-2 text-right">Footprint</th>
              {parcelFits && <th className="w-[125px] px-3 py-2 text-right">Parcel fit</th>}
              <th className="w-[115px] px-3 py-2 text-right">Working basis</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {matrix.rows.map((row) => {
              const isCurrent = row.kind === 'current';
              const generationAndCcsWater = row.water.generationConsumptionGalPerDay
                + row.water.ccsConsumptionGalPerDay;
              return (
                <tr
                  key={row.id}
                  className={`border-b border-l-4 border-b-border/70 ${rowTone(row)} ${isCurrent ? 'bg-primary/5' : 'bg-card'}`}
                  data-alternative-id={row.id}
                >
                  <th className="px-3 py-3 align-top" scope="row">
                    <span className="flex items-center gap-2 font-bold text-text-primary">
                      {isCurrent ? <CircleDashed className="h-3.5 w-3.5 text-primary" /> : <CheckCircle2 className="h-3.5 w-3.5 text-success" />}
                      {isCurrent
                        ? 'Current working basis'
                        : row.kind === 'equipment-count'
                          ? `${row.blockCount} × ${row.equipmentModel} envelope`
                          : `${row.blockCount}-block reserve duty`}
                    </span>
                    <span className="mt-1 block text-[9px] text-text-muted">
                      {isCurrent
                        ? 'Entered rating'
                        : row.kind === 'equipment-count'
                          ? 'Fixed OEM block rating'
                          : `Exact screen ${number(row.exactReserveMinimumIsoGrossMWPerUnit ?? 0, 1)} MW/block`}
                    </span>
                  </th>
                  <td className="px-3 py-3 text-right align-top">
                    <strong className="text-xs text-text-primary">{row.blockCount} × {number(row.isoGrossMWPerUnit)}</strong>
                    <span className="mt-1 block text-[9px] text-text-muted">{number(row.totalIsoGrossMW)} MW total</span>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <span className={`inline-flex border px-2 py-1 font-bold uppercase ${row.ccsEnabled ? 'border-success/40 bg-success/10 text-success' : 'border-border text-text-muted'} rounded-md`}>
                      {row.ccsEnabled ? 'On' : 'Off'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right align-top">
                    <strong className="text-xs">{number(row.capacity.nMinusOneNetMW, 1)} MW</strong>
                    <span className="mt-1 block text-[9px] text-text-muted">load {signed(row.capacity.nMinusOneMarginMW)} MW</span>
                  </td>
                  <td className="px-3 py-3 text-right align-top">
                    <strong className={row.capacity.nMinusOneReserveVerdict === 'pass' ? 'text-success' : 'text-danger'}>{signed(row.capacity.nMinusOneReserveMarginMW)} MW</strong>
                    <span className="mt-1 block text-[9px] uppercase text-text-muted">{row.capacity.nMinusOneReserveVerdict}</span>
                  </td>
                  <td className="px-3 py-3 text-right align-top">
                    <strong>{row.result.reliability.probabilityLoadServed == null
                      ? 'Not established'
                      : `${number(row.result.reliability.probabilityLoadServed * 100, 3)}%`}</strong>
                    <span className="mt-1 block text-[9px] text-text-muted">forced-outage screen</span>
                  </td>
                  <td className="px-3 py-3 text-right align-top">
                    <strong>{number(row.gas.peakDthPerDay)}</strong>
                    <span className="mt-1 block text-[9px] text-text-muted">Dth/day · full fleet</span>
                  </td>
                  <td className="px-3 py-3 text-right align-top">
                    <strong>{number(generationAndCcsWater)}</strong>
                    <span className="mt-1 block text-[9px] text-text-muted">gal/day · excludes DC</span>
                  </td>
                  <td className="px-3 py-3 text-right align-top">
                    <strong>{compactAnnualTonnes(row.emissions.capturedCo2TonnesPerYear)} / {compactAnnualTonnes(row.emissions.residualCo2TonnesPerYear)}</strong>
                    <span className="mt-1 block text-[9px] text-text-muted">t/year · on-site generation at full-load hours</span>
                  </td>
                  <td className="px-3 py-3 text-right align-top">
                    <strong>{number(row.footprint.totalAcres, 1)} ac</strong>
                    <span className="mt-1 block text-[9px] text-text-muted">screening land</span>
                  </td>
                  {parcelFits && (
                    <td className="px-3 py-3 text-right align-top" data-testid={`alternative-parcel-fit-${row.id}`}>
                      {(() => {
                        const fit = parcelFits.get(row.id);
                        if (!fit) return <span className="text-[9px] text-text-muted">—</span>;
                        return (
                          <>
                            <strong className={parcelFitTone(fit)}>{parcelFitLabel(fit)}</strong>
                            <span className="mt-1 block text-[9px] text-text-muted">
                              {fit.containmentPct != null ? `${number(fit.containmentPct, 1)}% contained` : 'current layout'}
                            </span>
                          </>
                        );
                      })()}
                    </td>
                  )}
                  <td className="px-3 py-3 text-right align-top">
                    <span className={`inline-flex border px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${isCurrent ? 'border-success/40 bg-success/10 text-success' : 'border-border text-text-muted'} rounded-md`}>
                      {isCurrent ? 'Current basis' : 'Available above'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </details>

      {matrix.rows[0]?.result.reliability.caveat && (
        <p
          className="border-t border-border px-4 py-2 text-[10px] text-text-secondary"
          data-testid="generation-alternatives-reliability-caveat"
        >
          <span className="mr-1 font-bold uppercase tracking-wider text-text-muted">Reliability screen</span>
          {matrix.rows[0].result.reliability.caveat}
        </p>
      )}

      <details className="border-t border-border px-4 py-3">
        <summary className="cursor-pointer text-[9px] font-bold uppercase tracking-wider text-text-muted">Held-constant engineering basis</summary>
        <ul className="mt-2 space-y-1 text-[10px] text-text-secondary">
          {matrix.heldConstantDisclosures.map((disclosure) => <li key={disclosure}>— {disclosure}</li>)}
        </ul>
      </details>
    </section>
  );
}
